import React, { useEffect, useMemo, useState } from "react";
import DateTimePicker from "../components/ui/DateTimePicker";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";

const emptyForm = {
  sailingId: "",
  vesselId: "",
  route: "",
  departurePort: "",
  arrivalPort: "",
  etd: "",
  eta: "",
  bookingCutoff: "",
  status: "Draft",
  freightCapacity: "",
  livestockCapacity: "",
  weightLimit: "",
  zones: [],
};

export default function NewSailing() {
  const [form, setForm] = useState(emptyForm);
  const [vessels, setVessels] = useState([]);
  const [loadingVessels, setLoadingVessels] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    async function loadVessels() {
      try {
        setLoadingVessels(true);
        const res = await fetch(`${API_BASE}/vessels`);
        const data = await res.json();

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];

        setVessels(list);
      } catch (err) {
        console.error(err);
        setMessage("Could not load vessels. Check API is running.");
      } finally {
        setLoadingVessels(false);
      }
    }

    loadVessels();
  }, []);

  const selectedVessel = useMemo(() => {
    return vessels.find((v) => String(v._id || v.id) === String(form.vesselId));
  }, [vessels, form.vesselId]);

  function handleChange(e) {
    const { name, value } = e.target;

    if (name === "vesselId") {
      const vessel = vessels.find((v) => String(v._id || v.id) === String(value));

      setForm((prev) => ({
        ...prev,
        vesselId: value,
        freightCapacity:
          vessel?.freightCapacity ??
          vessel?.capacity?.freight ??
          vessel?.freightLimit ??
          "",
        livestockCapacity:
          vessel?.livestockCapacity ??
          vessel?.capacity?.livestock ??
          vessel?.livestockLimit ??
          "",
        weightLimit:
          vessel?.weightLimit ??
          vessel?.capacity?.weight ??
          vessel?.maxWeightKg ??
          "",
        zones: vessel?.zones || vessel?.capacityZones || [],
      }));

      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function buildRoute() {
    if (!form.departurePort || !form.arrivalPort) return form.route;
    return `${form.departurePort} → ${form.arrivalPort}`;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setMessage("");

    if (!form.sailingId.trim()) {
      setMessage("Add a Sailing ID.");
      return;
    }

    if (!form.vesselId) {
      setMessage("Select a vessel.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        sailingRef: form.sailingId,
        vessel: form.vesselId,
        origin: form.departurePort,
        destination: form.arrivalPort,
        routeLabel: buildRoute(),
        etd: form.etd,
        eta: form.eta || null,
        bookingCutoff: form.bookingCutoff || null,
        status: String(form.status || "draft").toLowerCase(),
        capacity: {
          maxWeightKg: Number(form.weightLimit) || 0,
          maxCubeM3: Number(form.freightCapacity) || 0,
          maxLivestockHead: Number(form.livestockCapacity) || 0,
        },
        notes: "",
      };

      const res = await fetch(`${API_BASE}/sailings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "Failed to save sailing");
      }

      setMessage("Sailing saved successfully.");
      setForm(emptyForm);
    } catch (err) {
      console.error(err);
      setMessage(`Could not save sailing: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  const messageIsError =
    message &&
    (message.toLowerCase().includes("could not") ||
      message.toLowerCase().includes("failed") ||
      message.toLowerCase().includes("select") ||
      message.toLowerCase().includes("add"));

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1600px] px-8 py-8 lg:px-10">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-950">
              New Sailing
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Create a new sailing schedule and pull through vessel capacity defaults.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setForm(emptyForm)}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Reset
            </button>

            <button
              type="submit"
              form="new-sailing-form"
              disabled={saving}
              className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Sailing"}
            </button>
          </div>
        </div>

        {message ? (
          <div
            className={`mb-6 rounded-2xl border px-4 py-3 text-sm shadow-sm ${
              messageIsError
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}
          >
            {message}
          </div>
        ) : null}

        <form id="new-sailing-form" onSubmit={handleSubmit}>
          <div className="grid gap-6 xl:grid-cols-[1.75fr_0.85fr]">
            <div className="space-y-6">
              <SectionCard
                title="Sailing Details"
                description="Core sailing info, vessel assignment, route, and schedule."
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                  <Field label="Sailing ID">
                    <input
                      name="sailingId"
                      value={form.sailingId}
                      onChange={handleChange}
                      placeholder="e.g. SS-2026-001"
                      className="input"
                    />
                  </Field>

                  <Field label="Vessel">
                    <select
                      name="vesselId"
                      value={form.vesselId}
                      onChange={handleChange}
                      className="input"
                    >
                      <option value="">
                        {loadingVessels ? "Loading vessels..." : "Select Vessel"}
                      </option>
                      {vessels.map((v) => (
                        <option key={v._id || v.id} value={v._id || v.id}>
                          {v.name || v.vesselName || "Unnamed Vessel"}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Status">
                    <select
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                      className="input"
                    >
                      <option>Draft</option>
                      <option>Open</option>
                      <option>Closed</option>
                      <option>Departed</option>
                      <option>Arrived</option>
                      <option>Cancelled</option>
                    </select>
                  </Field>

                  <Field label="Departure Port">
                    <PortSelect name="departurePort" value={form.departurePort} onChange={handleChange} onCustom={(val) => setForm(p => ({ ...p, departurePort: val }))} />
                  </Field>

                  <Field label="Arrival Port">
                    <PortSelect name="arrivalPort" value={form.arrivalPort} onChange={handleChange} onCustom={(val) => setForm(p => ({ ...p, arrivalPort: val }))} />
                  </Field>

                  <Field label="Route">
                    <input
                      name="route"
                      value={buildRoute()}
                      onChange={handleChange}
                      placeholder="Auto generated"
                      className="input bg-slate-50 text-slate-500"
                      readOnly
                    />
                  </Field>

                  <Field label="ETD">
                    <DateTimePicker
                      value={form.etd}
                      onChange={(val) => setForm((prev) => ({ ...prev, etd: val }))}
                    />
                  </Field>

                  <Field label="ETA">
                    <DateTimePicker
                      value={form.eta}
                      onChange={(val) => setForm((prev) => ({ ...prev, eta: val }))}
                    />
                  </Field>

                  <Field label="Booking Cutoff">
                    <DateTimePicker
                      value={form.bookingCutoff}
                      onChange={(val) => setForm((prev) => ({ ...prev, bookingCutoff: val }))}
                    />
                  </Field>
                </div>
              </SectionCard>

              <SectionCard
                title="Capacity Settings"
                description="Capacity values are pulled from the selected vessel and can be adjusted."
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                  <Field label="Freight Capacity">
                    <input
                      name="freightCapacity"
                      type="number"
                      value={form.freightCapacity}
                      onChange={handleChange}
                      placeholder="e.g. 40"
                      className="input"
                    />
                  </Field>

                  <Field label="Livestock Capacity">
                    <input
                      name="livestockCapacity"
                      type="number"
                      value={form.livestockCapacity}
                      onChange={handleChange}
                      placeholder="e.g. 1200"
                      className="input"
                    />
                  </Field>

                  <Field label="Weight Limit KG">
                    <input
                      name="weightLimit"
                      type="number"
                      value={form.weightLimit}
                      onChange={handleChange}
                      placeholder="e.g. 250000"
                      className="input"
                    />
                  </Field>
                </div>
              </SectionCard>

              <SectionCard
                title="Zone Availability"
                description="Preview configured vessel zones for this sailing."
              >
                {!selectedVessel ? (
                  <EmptyState text="Select a vessel to preview zones." />
                ) : form.zones.length === 0 ? (
                  <EmptyState text="This vessel has no zones configured yet." />
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {form.zones.map((zone, index) => (
                      <div
                        key={zone._id || zone.id || index}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <p className="font-semibold text-slate-900">
                          {zone.name || zone.zoneName || `Zone ${index + 1}`}
                        </p>
                        <div className="mt-3 space-y-1 text-sm text-slate-600">
                          <p>Type: {zone.type || "General"}</p>
                          <p>Capacity: {zone.capacity || zone.limit || "Not set"}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>

            <div className="space-y-6">
              <AsideCard title="Summary">
                <SummaryRow label="Sailing ID" value={form.sailingId || "—"} />
                <SummaryRow
                  label="Vessel"
                  value={
                    selectedVessel?.name ||
                    selectedVessel?.vesselName ||
                    "Not selected"
                  }
                />
                <SummaryRow label="Route" value={buildRoute() || "—"} />
                <SummaryRow label="Status" value={form.status || "Draft"} />
                <SummaryRow label="ETD" value={form.etd || "—"} />
                <SummaryRow label="ETA" value={form.eta || "—"} />
                <SummaryRow
                  label="Booking Cutoff"
                  value={form.bookingCutoff || "—"}
                />
              </AsideCard>

              <AsideCard title="Capacity Overview">
                <SummaryRow
                  label="Freight Capacity"
                  value={form.freightCapacity || "—"}
                />
                <SummaryRow
                  label="Livestock Capacity"
                  value={form.livestockCapacity || "—"}
                />
                <SummaryRow
                  label="Weight Limit KG"
                  value={form.weightLimit || "—"}
                />
                <SummaryRow
                  label="Zones"
                  value={String(form.zones?.length || 0)}
                />
              </AsideCard>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.9rem;
          border: 1px solid rgb(203 213 225);
          background: white;
          padding: 0.75rem 0.95rem;
          font-size: 0.95rem;
          color: rgb(15 23 42);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }

        .input::placeholder {
          color: rgb(148 163 184);
        }

        .input:hover {
          border-color: rgb(148 163 184);
        }

        .input:focus {
          border-color: rgb(15 23 42);
          box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.08);
        }
      `}</style>
    </div>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
        {description ? (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function AsideCard({ title, children }) {
  return (
    <aside className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
        {title}
      </h2>
      <div className="mt-4 space-y-3">{children}</div>
    </aside>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <span className="max-w-[60%] break-words text-right text-sm font-semibold text-slate-900">
        {value}
      </span>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
      {text}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-slate-700">
        {label}
      </span>
      {children}
    </label>
  );
}

const NZ_PORTS = [
  { group: "North Island", ports: ["Napier", "Wellington", "Auckland", "Tauranga", "Whangarei", "Whanganui", "New Plymouth"] },
  { group: "South Island", ports: ["Timaru", "Lyttelton", "Nelson", "Picton", "Dunedin", "Bluff", "Westport", "Greymouth"] },
  { group: "Chatham Islands", ports: ["Chatham Islands (Waitangi)", "Pitt Island"] },
];

const KNOWN_PORTS = NZ_PORTS.flatMap(g => g.ports);

function PortSelect({ name, value, onChange, onCustom }) {
  const isCustom = value && !KNOWN_PORTS.includes(value);
  const [showCustom, setShowCustom] = React.useState(isCustom);

  const handleSelectChange = (e) => {
    if (e.target.value === "__other__") {
      setShowCustom(true);
      onCustom("");
    } else {
      setShowCustom(false);
      onChange(e);
    }
  };

  return (
    <div className="space-y-2">
      <select
        name={name}
        value={showCustom ? "__other__" : (value || "")}
        onChange={handleSelectChange}
        className="input"
      >
        <option value="">— Select port —</option>
        {NZ_PORTS.map(({ group, ports }) => (
          <optgroup key={group} label={group}>
            {ports.map(port => (
              <option key={port} value={port}>{port}</option>
            ))}
          </optgroup>
        ))}
        <option value="__other__">Other (specify below)</option>
      </select>

      {showCustom && (
        <input
          className="input"
          placeholder="Enter port name..."
          value={isCustom ? value : ""}
          onChange={(e) => onCustom(e.target.value)}
          autoFocus
        />
      )}
    </div>
  );
}